import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useRef,
} from "react";
import {
  callErrorToast,
  callSuccessToast,
  cleanObject,
  removeError,
} from "../../../utilities/utilities.ts";
import { UsersService } from "../../../services/user/users.ts";
import { ErrorResponse } from "../../../Models/Error.js";
import {
  ResetPasswordUser,
  validateResetPassword,
} from "../../../Models/User.ts";
import { useAppContext } from "../../../AppProvider.jsx";

const usersService = new UsersService();

interface ResetPasswordUserProviderProps {
  id: string;
  children: ReactNode;
  callback?: () => void;
}

interface ResetPasswordUserContextValue {
  loader: boolean;
  edit: (
    user: ResetPasswordUser,
    property: keyof ResetPasswordUser | null,
    save?: boolean
  ) => void;
  data: ResetPasswordUser;
  callback?: () => void;
  error: ErrorResponse<ResetPasswordUser> | null;
}

const ResetPasswordUserContext = createContext<
  ResetPasswordUserContextValue | undefined
>(undefined);

const ResetPasswordUserProvider: React.FC<ResetPasswordUserProviderProps> = ({
  id,
  children,
  callback,
}) => {
  const { dictionary } = useAppContext();
  const [data, setData] = useState<ResetPasswordUser>({
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState<ErrorResponse<ResetPasswordUser> | null>(
    null
  );
  const [loader, setLoader] = useState<boolean>(false);

  const prevError = useRef<ErrorResponse<ResetPasswordUser>>(null);

  const edit = (
    resetPasswordUser: ResetPasswordUser,
    property: keyof ResetPasswordUser | null,
    save = true
  ): void => {
    resetPasswordUser = cleanObject(resetPasswordUser);
    if (property) {
      removeError(property, error, prevError, setError);
    }

    if (save) {
      const validation = validateResetPassword(data);
      if (!validation.isValid) {
        setError({ response: { data: validation.errors } });
        return;
      }

      setLoader(true);
      usersService
        .resetPassword(id, data)
        .then((res) => {
          setData(res.data);
          setLoader(false);
          if (callback) {
            callback();
          }
          callSuccessToast(dictionary.messages.save_successful);
        })
        .catch((err) => {
          callErrorToast(err);
          setLoader(false);
          setError(err);
        });
      return;
    }
    setData(resetPasswordUser);
  };

  return (
    <ResetPasswordUserContext.Provider
      value={{ loader, edit, callback, data, error }}
    >
      {children}
    </ResetPasswordUserContext.Provider>
  );
};

const useResetPasswordUserContext = (): ResetPasswordUserContextValue => {
  const context = useContext(ResetPasswordUserContext);
  if (!context) {
    throw new Error(
      "useResetPasswordUserContext must be used within a ResetPasswordUserProvider"
    );
  }
  return context;
};

export { ResetPasswordUserProvider, useResetPasswordUserContext };
